import { ContractStatusEnum, IContract } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { memo } from 'react';
import {
  CONTRACT_STATUS_ACCENT_COLOR,
  CONTRACT_STATUS_DISPLAY,
} from '../../../constants/contracts';
import { getTermAriaLabel } from '../../../utils/contractLineage';
import { MBox, MHStack, MIDCopyBox, MText } from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';
import { ContractTermLabel } from './ContractTermLabel';

const CURRENT_TERM_RING = 'inset 0 0 0 1px var(--chakra-colors-border-accent)';

interface ContractTermChipProps {
  contract: IContract;
  termNumber: number;
  termCount: number;
  isCurrent: boolean;
  isUpcoming: boolean;
  /** Omit to render the term as disabled */
  onSelect?: (contractId: string) => void;
}

export const ContractTermChip = memo(
  ({
    contract,
    termNumber,
    termCount,
    isCurrent,
    isUpcoming,
    onSelect,
  }: ContractTermChipProps) => {
    const { id, status, startDate, endDate, totalValue, currency } = contract;
    const isPending = status === ContractStatusEnum.PENDING;
    const accentColor = CONTRACT_STATUS_ACCENT_COLOR[status];

    // The ID copies on click, so it sits beside the term's button rather than
    // inside it; the container's hover and focus follow the button alone
    return (
      <MBox
        data-current={isCurrent || undefined}
        position="relative"
        flexShrink={0}
        minW="210px"
        borderRadius="md"
        borderWidth="1px"
        borderStyle={isPending ? 'dashed' : 'solid'}
        borderColor={isCurrent ? 'border.accent' : 'border.default'}
        // Thickens the accent border to 2px without shifting layout
        boxShadow={isCurrent ? CURRENT_TERM_RING : undefined}
        bg={status === ContractStatusEnum.FINISHED ? 'bg.canvas' : 'bg.surface'}
        transition="box-shadow 0.12s ease, border-color 0.12s ease"
        _before={{
          content: '""',
          position: 'absolute',
          left: '-1px',
          top: '-1px',
          bottom: '-1px',
          borderLeftWidth: '4px',
          borderLeftStyle: 'solid',
          borderLeftColor: accentColor,
          borderLeftRadius: 'md',
        }}
        css={{
          '&:has(> button:enabled:hover)': { borderColor: 'border.accent' },
          '&:has(> button:focus-visible)': {
            boxShadow: isCurrent
              ? `${CURRENT_TERM_RING}, var(--chakra-shadows-focus)`
              : 'focus',
          },
        }}
      >
        <MBox
          asChild
          data-testid={`contract-term-chip-${id}`}
          aria-label={getTermAriaLabel(contract, termNumber, termCount, {
            isCurrent,
            isUpcoming,
          })}
          aria-current={isCurrent || undefined}
          onClick={onSelect ? () => onSelect(id) : undefined}
          display="block"
          w="100%"
          textAlign="left"
          px={3}
          pt={2}
          pl={4}
          cursor={onSelect ? 'pointer' : 'default'}
          _focusVisible={{ outline: 'none' }}
        >
          <button type="button" disabled={!onSelect}>
            <ContractTermLabel
              termNumber={termNumber}
              isCurrent={isCurrent}
              isUpcoming={isUpcoming}
            />
            <MText fontWeight="semibold" color="fg.strong" whiteSpace="nowrap">
              {toDateShort(startDate)} – {toDateShort(endDate)}
            </MText>
            <MHStack gap={2} fontSize="xs" color="fg.muted" mt={0.5}>
              <MText
                as="span"
                fontSize="inherit"
                fontWeight="semibold"
                color={
                  status === ContractStatusEnum.FINISHED
                    ? 'fg.muted'
                    : accentColor
                }
              >
                {CONTRACT_STATUS_DISPLAY[status]}
              </MText>
              <MCurrency
                as="span"
                amount={totalValue}
                options={{ currency }}
                fontSize="inherit"
                fontWeight="semibold"
                color="fg.primary"
              />
            </MHStack>
          </button>
        </MBox>
        <MBox px={3} pl={4} pt={0.5} pb={2}>
          <MIDCopyBox copyValue={id} displayIcon={false} fontSize="xs" />
        </MBox>
      </MBox>
    );
  },
);
